import type { FocusSelection } from "@models/Chart";
import type { GuardrailRollingOption, PointInTimeViewerOptions } from "@models/Dataset";
import type { VisualizationBase } from "@models/VisualizationBase";
import type { WidgetRollup } from "@models/Rollup";
import type { MetricBase, MetricRepresentedResult } from "@models/Metric";
import { DEFAULT_METRIC_BASE } from "@models/Metric";

/** Whether brand candidates come from a one-dimensional question's response
 *  categories (Single → values, Multiple → variables) or from a Single grid
 *  question's Grid Variables. */
export type ScorecardSourceQuestionKind = "one_dimensional" | "single_grid";

/** A candidate brand detected from the Source Brand Question, before the
 *  researcher assigns it a Primary/Competitor role. */
export interface ScorecardBrandCandidate {
  /** Stable source id: a Value.id (one-dim Single) or Variable.id (Multiple / grid). */
  sourceId: string;
  label: string;
  /** Every label variant (e.g. rowtitle and title) this candidate could be
   *  echoed back as by the response/data endpoint — the question-detail
   *  endpoint and the response endpoint don't always agree on which field
   *  they surface, so matching must try all of them, not just `label`. */
  matchLabels: string[];
  /** Preserves the source question's order for default column sequencing. */
  sourceOrder: number;
}

/** A brand the researcher has added to the Scorecard. */
export interface ScorecardBrand {
  id: string;
  role: "primary" | "competitor";
  sourceId: string;
  label: string;
  sourceOrder: number;
}

export interface ScorecardSourceQuestion {
  questionId: string;
  questionLabel: string;
  questionTitle: string;
  questionType: "SINGLE" | "MULTIPLE";
  kind: ScorecardSourceQuestionKind;
}

export type ScorecardMappingStatus =
  | "matched"
  | "manual"
  | "unresolved"
  | "unavailable";

/** A response-side candidate (a Value or Variable) that a row's brand
 *  mappings can bind to — the row's own equivalent of ScorecardBrandCandidate.
 *  Shared by Question rows and Metric rows (see ScorecardDatasetMetricRow). */
export type ScorecardResponseCategory = MetricRepresentedResult;

/** Per-brand mapping outcome for a single row (Question or Metric). */
export interface ScorecardBrandMapping {
  brandId: string;
  status: ScorecardMappingStatus;
  /** Response-side id: a Value.id (one-dim Single) or Variable.id (Multiple / grid). */
  boundResponseId: string | null;
  boundResponseLabel: string | null;
  /** Label variants to try when matching this brand's cell in fetched response
   *  data — see ScorecardBrandCandidate.matchLabels. Absent on mappings
   *  persisted before this field existed; callers fall back to boundResponseLabel. */
  matchLabels?: string[] | null;
  /** Optional researcher-entered explanation, only used when status is "unavailable". */
  unavailableReason?: string | null;
}

/** Shared by both row kinds — a Question row's or a saved dataset Metric's
 *  own resolved result type. */
export type ScorecardRowResultType = "percentage" | "mean";

/** The fields needed to define a percentage/mean metric off a Single/Multiple/
 *  Single-grid/Number/Float question directly inside the Scorecard editor:
 *  the source question plus one response value or one SUM_SELECTED rollup,
 *  and that question's own resolved brand mappings. Shared by a Question
 *  row's own definition and by a Scorecard Row Base's New Question Base,
 *  which need the identical shape. */
export interface ScorecardQuestionRowDefinition {
  sourceQuestionId: string;
  sourceQuestionLabel: string;
  sourceQuestionTitle: string;
  /** Normalized underlying question type. Question rows can be any of the
   *  four (Number/Float produce Mean rows); a New Question Base is always
   *  SINGLE or MULTIPLE since eligibility restricts it to percentage
   *  questions. */
  sourceQuestionType: "SINGLE" | "MULTIPLE" | "NUMBER" | "FLOAT";
  sourceQuestionKind: ScorecardSourceQuestionKind;
  valueFocus: FocusSelection | null;
  metricValueLabel: string | null;
  metricValueCode: string | null;
  rollup: WidgetRollup | null;
  rollupValueIds: string[] | null;

  /** One entry per ScorecardBrand, resolved against this definition's own source question. */
  brandMappings: ScorecardBrandMapping[];
  responseCategories?: ScorecardResponseCategory[];
}

/** A New Question Base: an independent percentage metric used only for
 *  Scorecard Row Base qualification. Never rendered as its own Scorecard
 *  row — stored inside the owning ScorecardQuestionRow as a non-displayed
 *  configuration item. */
export interface ScorecardNewQuestionBase extends ScorecardQuestionRowDefinition {
  /** Independent stable identity from the owning row's id, so duplicating a
   *  row can give this its own new id and the researcher can edit it
   *  without disturbing the source row's New Question Base. */
  id: string;
}

/** Scorecard Row Base sourced from another already-defined Question row:
 *  brand-specific qualification is derived from that row's own brand
 *  dimension (crosstab-style diagonal — each brand qualifies against its
 *  own value on the referenced row's question). */
export interface ScorecardRowBaseExistingQuestionRow {
  kind: "existing_question_row";
  /** Stable id of the referenced ScorecardQuestionRow. */
  questionRowId: string;
  /** Cached display label of the referenced row, refreshed on every save of
   *  that row — display-only, never authoritative for engine identity. */
  questionRowLabel: string;
}

/** Scorecard Row Base sourced from a New Question Base: the same qualifying
 *  condition applies uniformly to every brand column. */
export interface ScorecardRowBaseNewQuestionBase {
  kind: "new_question_base";
  questionBase: ScorecardNewQuestionBase;
}

export type ScorecardRowBase =
  | ScorecardRowBaseExistingQuestionRow
  | ScorecardRowBaseNewQuestionBase;

export interface ScorecardQuestionRow extends ScorecardQuestionRowDefinition {
  id: string;
  type: "question";
  label: string;
  resultType: ScorecardRowResultType;

  visualizationBase?: VisualizationBase;
  /** Brand-specific respondent qualification — absent/null means no Base. */
  rowBase?: ScorecardRowBase | null;
  /** One-shot flag: true only immediately after this row's Base was
   *  force-cleared by deleting the Question row it referenced. Cleared the
   *  next time this row is saved through the builder, whether or not a new
   *  Base is chosen — it signals "needs attention," not a persistent state. */
  rowBaseNeedsAttention?: boolean;
}

/** A row referencing a saved Dataset Metric (see DatasetMetric in
 *  models/Metric.ts) — the metric's own definition (source question, focus,
 *  population, Base) is read-only here and always resolved live from
 *  `dataset.metrics`; only the local label override and this Scorecard's own
 *  Brand Mappings are owned by the row itself. */
export interface ScorecardDatasetMetricRow {
  id: string;
  type: "metric";
  /** Stable reference to the saved DatasetMetric.id this row was built from. */
  datasetMetricId: string;
  /** Snapshot of the metric's name at selection/save time — the row's
   *  default label, and a display fallback if the metric can no longer be
   *  resolved from `dataset.metrics`. Never authoritative over the live metric. */
  datasetMetricName: string;
  /** Local override label — defaults to datasetMetricName, editable independently. */
  label: string;
  /** Mirrors the referenced metric's own resultType. */
  resultType: ScorecardRowResultType;
  /** One entry per represented result (see dataset-metric-row-utils.ts),
   *  bound to this Scorecard's brands. */
  brandMappings: ScorecardBrandMapping[];
  /** Cached "represented results" candidates the brand mappings above bind
   *  against — same shape/role as ScorecardQuestionRowDefinition.responseCategories. */
  responseCategories?: ScorecardResponseCategory[];

  /** Everything below is a fetch-relevant snapshot of the metric's own
   *  definition, resolved once at builder-save time (see
   *  dataset-metric-row-utils.ts's buildDatasetMetricRowFetchFields) so
   *  rendering never needs to re-fetch the live DatasetMetric — same
   *  resilience rationale as ScorecardQuestionRowDefinition's cached focus
   *  fields. */
  sourceQuestionId: string;
  sourceQuestionKind: ScorecardSourceQuestionKind;
  focusMode: "VALUE" | "VARIABLE" | null;
  metricValueCode: string | null;
  rollup: WidgetRollup | null;
  /** Snapshot of the metric's own saved Base at selection/save time. */
  base: MetricBase;
  /** Mirrors the metric's own Metric population choice: true when its
   *  population mode is "total_survey_population", false when it's
   *  "valid_question_respondents" (always false for Mean metrics, since that
   *  mode is fixed for them). Tells the search request to use the total
   *  survey population as the Base rather than validating it against the
   *  source question's own respondent pool — a Scorecard Metric-row-only
   *  override; every other request path leaves this false. */
  useTotalBase: boolean;
}

export interface ScorecardHeadingRow {
  id: string;
  type: "heading";
  label: string;
}

export type ScorecardRow =
  | ScorecardQuestionRow
  | ScorecardDatasetMetricRow
  | ScorecardHeadingRow;

export type ScorecardWeightSelection = "DISABLED" | "DATASET_DEFAULT";

/** Matches the shared TimePeriod shape (Chart.tsx / table-config-slice.ts / etc). */
export type ScorecardTimePeriod =
  | "all_time"
  | "custom_period"
  | "latest_month"
  | "previous_month"
  | "latest_quarter"
  | "previous_quarter"
  | "latest_semester"
  | "previous_semester";

export interface ScorecardTimePeriodSelection {
  timePeriod: ScorecardTimePeriod;
  startDate: string | null;
  endDate: string | null;
}

export interface ScorecardRollingAverageConfig {
  enabled: boolean;
  windowSize: GuardrailRollingOption;
}

export interface ScorecardConfig {
  title: string;
  subtitle: string;

  sourceQuestion: ScorecardSourceQuestion | null;
  brandCandidates: ScorecardBrandCandidate[];
  /** Ordered: Primary first, then Competitors in source-question order. */
  brands: ScorecardBrand[];
  /** Researcher-chosen display order for brand columns (ids from `brands`,
   *  Primary included — Primary can be moved to any position). Possibly
   *  stale or incomplete (a brand added/removed since, or a config saved
   *  before this field existed); always reconcile via getOrderedBrands
   *  rather than reading directly. */
  columnOrder: string[];
  /** Brand ids hidden from the rendered Scorecard table, Primary included —
   *  display only, never strips the brand's row mappings/data. At least one
   *  brand must always stay visible. */
  hiddenBrandIds: string[];
  /** Header text for the first (metric/row-label) column. Blank/whitespace
   *  falls back to "Metric" at render time — see ScorecardChart. */
  metricColumnHeader: string;
  /** Custom highlight color (hex) for whichever brand currently holds the
   *  Primary role — follows the role across reorders and Primary changes.
   *  Null/undefined uses the default theme blue. */
  primaryBrandHighlightColor: string | null;

  rows: ScorecardRow[];

  weight: ScorecardWeightSelection;
  percentageFormatting: 0 | 1 | 2;
  meanFormatting: 0 | 1 | 2;
  timePeriodSelection: ScorecardTimePeriodSelection;
  rollingAverage: ScorecardRollingAverageConfig;
  viewerOptions: PointInTimeViewerOptions;
  responsesMinDate?: string;
  responsesMaxDate?: string;
}

export function isQuestionRow(row: ScorecardRow): row is ScorecardQuestionRow {
  return row.type === "question";
}

export function isDatasetMetricRow(
  row: ScorecardRow,
): row is ScorecardDatasetMetricRow {
  return row.type === "metric";
}

export function isHeadingRow(row: ScorecardRow): row is ScorecardHeadingRow {
  return row.type === "heading";
}

/** Migrates a legacy Scorecard Row Base shape (pre-Question/Metric rename)
 *  to the current one. Old and new shapes are distinguished by `kind`, so
 *  this is safe to run on already-current data too (no-ops). */
function normalizeRowBase(
  raw: unknown,
): ScorecardRowBase | null | undefined {
  if (raw === null || raw === undefined) return raw as null | undefined;
  const base = raw as Record<string, unknown>;
  if (base.kind === "existing_row") {
    return {
      kind: "existing_question_row",
      questionRowId: (base.questionRowId ?? base.metricRowId) as string,
      questionRowLabel: (base.questionRowLabel ?? base.metricRowLabel) as string,
    };
  }
  if (base.kind === "new_metric") {
    return {
      kind: "new_question_base",
      questionBase: (base.questionBase ??
        base.metric) as ScorecardNewQuestionBase,
    };
  }
  return raw as ScorecardRowBase;
}

/**
 * Backward-compatible row hydration for persisted ScorecardConfigs saved
 * before the Question/Metric rename: rows used `type: "metric"` for what is
 * now the Question row (source-question-driven), and the new
 * ScorecardDatasetMetricRow (a reference to a saved Dataset Metric) now owns
 * that literal going forward. The two are disambiguated by the presence of
 * `datasetMetricId` — only ever set on a genuine new-format Metric row —
 * so this never needs a one-time backend migration. Defensive by design:
 * runs on raw persisted JSON, never throws on an unexpected shape.
 */
export function normalizeScorecardRow(raw: unknown): ScorecardRow {
  if (!raw || typeof raw !== "object") return raw as ScorecardRow;
  const row = raw as Record<string, unknown>;

  if (row.type === "heading") {
    return row as unknown as ScorecardHeadingRow;
  }

  if (row.type === "metric" && typeof row.datasetMetricId === "string") {
    // Defensively default the fetch-relevant fields added after the initial
    // Metric-row slice, so any row saved in that brief window still renders
    // (just without a live value) instead of crashing the fetch hook.
    const metricRow = row as unknown as ScorecardDatasetMetricRow;
    // Rows persisted before the enableBaseValidation → useTotalBase backend
    // rename cached the old (inverted) field — fall back to its logical NOT
    // before falling back to the true default for rows that predate the
    // field entirely.
    const legacyRow = row as unknown as { enableBaseValidation?: boolean };
    const useTotalBase =
      metricRow.useTotalBase ??
      (legacyRow.enableBaseValidation !== undefined ? !legacyRow.enableBaseValidation : true);
    return {
      ...metricRow,
      sourceQuestionId: metricRow.sourceQuestionId ?? "",
      sourceQuestionKind: metricRow.sourceQuestionKind ?? "one_dimensional",
      focusMode: metricRow.focusMode ?? null,
      metricValueCode: metricRow.metricValueCode ?? null,
      rollup: metricRow.rollup ?? null,
      base: metricRow.base ?? DEFAULT_METRIC_BASE,
      useTotalBase,
    };
  }

  // Either an already-current "question" row, or a legacy row still tagged
  // "metric" (pre-rename) — both hydrate through the same Question-row shape.
  return {
    ...(row as unknown as ScorecardQuestionRow),
    type: "question",
    rowBase: normalizeRowBase(row.rowBase ?? row.metricBase) ?? null,
    rowBaseNeedsAttention: (row.rowBaseNeedsAttention ??
      row.metricBaseNeedsAttention) as boolean | undefined,
  };
}
